import { Close, Groups, Person, Save } from '@mui/icons-material';
import {
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { addActivityToTheWatchlist } from '../../../api/activities-nim';
import { DocumentType, EgroupDTO, EscalationTypeCode, FavouriteActivityReadDTO, PersonReadDTO } from '../../../api/dto';
import { FormGenerator } from '../../FormGenerator';
import {
    ConditionalEscalationSelector,
    ConditionalEscalationType,
    getConditionalEscalationTypeProperties,
} from '../../schemas/addSignatureTask';
import { TFormElement } from '../../types';
import { UiSchemaType } from '../../UiSchemaType';

interface WatchlistDialogProps {
    activityId: number | undefined;
    handleClose: () => void;
    open: boolean;
    watchersData: FavouriteActivityReadDTO[] | undefined;
    readOnly: boolean;
    refetchWatchersData: () => void;
}

export const WatchlistDialog: React.FC<WatchlistDialogProps> = ({
    activityId,
    handleClose,
    open,
    watchersData,
    readOnly,
    refetchWatchersData,
}) => {
    const intl = useIntl();
    const queryClient = useQueryClient();
    const form = useForm<{ newWatchers: { type: ConditionalEscalationType; value?: PersonReadDTO | EgroupDTO }[] }>({
        mode: 'onSubmit',
        defaultValues: { newWatchers: [] },
    });
    const formRootElement: TFormElement = useMemo(
        () => ({
            type: UiSchemaType.FIELD_ARRAY,
            path: 'newWatchers',
            inline: true,
            element: {
                type: UiSchemaType.LAYOUT_VERTICAL,
                elements: [
                    {
                        type: UiSchemaType.AUTOCOMPLETE,
                        label: intl.formatMessage({ id: 'document.watchlist.select.type' }),
                        path: 'type',
                        required: true,
                        typeProperties: getConditionalEscalationTypeProperties(intl, false),
                    },
                    {
                        type: UiSchemaType.CUSTOM,
                        Component: (props) => (
                            <ConditionalEscalationSelector
                                {...props}
                                typePath={`${(props as any).path}.type`}
                                valuePath={`${(props as any).path}.value`}
                            />
                        ),
                    },
                ],
            },
            appendLabel: intl.formatMessage({ id: 'document.watchlist.addNewWatcher' }),
            removeLabel: intl.formatMessage({ id: 'document.watchlist.removeNewWatcher' }),
        }),
        [intl]
    );
    useEffect(() => {
        if (open) {
            form.reset({ newWatchers: [] });
        }
    }, [open, form]);
    const { mutate: mutateAddWatchers, isLoading } = useMutation(
        async (data: { personIds: number[] | undefined; egroupsIds: number[] | undefined }) => {
            if (activityId) {
                await addActivityToTheWatchlist({
                    id: activityId,
                    personIds: data.personIds,
                    egroupsIds: data.egroupsIds,
                });
                await refetchWatchersData();
            }
        },
        {
            onSuccess: () => {
                form.reset({ newWatchers: [] });
                //refetch infolines
                queryClient.invalidateQueries(['workflows', DocumentType.ACTIVITY, activityId]);
            },
            onError: () => toast.error(intl.formatMessage({ id: 'document.watchlist.error' })),
        }
    );
    const submit = form.handleSubmit((data) => {
        const personIds = data.newWatchers
            .filter(({ type }) => type.value === EscalationTypeCode.PERSON)
            .map(({ value }) => value?.id)
            .filter((id): id is number => id !== undefined);

        const egroupsIds = data.newWatchers
            .filter(({ type }) => type.value === EscalationTypeCode.EGROUP)
            .map(({ value }) => value?.id)
            .filter((id): id is number => id !== undefined);
        mutateAddWatchers({ personIds, egroupsIds });
    });
    const watchNewWatchers = form.watch('newWatchers');
    const canSubmit = watchNewWatchers.length > 0 && watchNewWatchers.every((a) => !!a && !!a.value);

    return (
        <Dialog onClose={!isLoading ? handleClose : undefined} open={open} fullWidth maxWidth="sm">
            <DialogTitle>
                <FormattedMessage
                    id={!readOnly ? 'document.watchlist.manageWatchers' : 'document.watchlist.manageWatchersReadOnly'}
                />
                <IconButton
                    onClick={handleClose}
                    disabled={isLoading}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={2}>
                    <List dense>
                        {watchersData?.map((watcher) => {
                            const isPerson = !!watcher.person;
                            const id = isPerson ? watcher.person?.id : watcher.egroup?.id;
                            const label = isPerson ? watcher.person?.searchLabel : watcher.egroup?.displayName;

                            return (
                                <ListItem key={id}>
                                    <ListItemIcon>{isPerson ? <Person /> : <Groups />}</ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{ minHeight: 24 }}
                                            >
                                                <Typography variant="body2">{label}</Typography>
                                                {!!watcher.person?.orgUnit && (
                                                    <Chip size="small" label={watcher.person.orgUnit.code} />
                                                )}
                                            </Stack>
                                        }
                                        sx={{ my: 0 }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>

                    {!!mutateAddWatchers && !readOnly && (
                        <FormProvider {...form}>
                            <FormGenerator rootElement={formRootElement} form={form} disabled={isLoading} />
                            {watchNewWatchers.length > 0 && (
                                <>
                                    <Button
                                        variant="contained"
                                        startIcon={<Save />}
                                        onClick={submit}
                                        disabled={isLoading || !canSubmit}
                                    >
                                        <FormattedMessage id="document.action.save" />
                                    </Button>
                                </>
                            )}
                        </FormProvider>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
